import { DownOutlineIcon } from '@carboncopyco/icons';
import { TextCaption, TooltipBubble } from '@carboncopyco/ui';
import type { ReactNode } from 'react';
import { CloseIcon } from '../../../../../assets/svg/CloseIcon';
import { tooltipHackClasses } from '../../../constants';
import useMoreTabsDropdown from '../../../hooks/useMoreTabsDropdown';
import type { AppInnerTabsItem } from './types';
import { getCloseIconClasses } from './utils';

const MoreInnerTabsDropdown = ({
  items,
  activeTabId,
  onTabClick,
  onTabClose,
  dropdownIcon,
}: {
  items: AppInnerTabsItem[];
  activeTabId: number;
  onTabClick: (tabId: number) => void;
  onTabClose: (tabId: number) => void;
  dropdownIcon?: ReactNode;
}) => {
  const { isOpen, handleIsOpen } = useMoreTabsDropdown();

  return (
    <div
      className="flex justify-center flex-grow relative z-200 border-s border-[var(--ds-inner-tab-inactive-bg)]"
      onMouseEnter={() => handleIsOpen(true)}
      onMouseLeave={() => handleIsOpen(false)}
    >
      <button
        type="button"
        className="flex items-center justify-center w-10 cursor-pointer flex-grow !bg-[var(--ds-inner-tab-inactive-bg)]"
        data-testid="inner-tab-menu-trigger"
      >
        {dropdownIcon || <DownOutlineIcon color="#000000" />}
      </button>
      {isOpen && (
        <div className="absolute top-full end-0 pt-[var(--ds-tab-sizing-dropdown-gap)] pe-px">
          <ul className="grid gap-1 bg-ds-accent3-100 rounded-md shadow-xl p-1 overflow-y-auto max-h-[calc(100vh-var(--ds-tab-sizing-height)-var(--ds-inner-tab-sizing-height)-100px)] !bg-[var(--ds-inner-tab-inactive-bg)]">
            {items.map((item) => {
              const isActiveTab = item.id === activeTabId;

              return (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={() => onTabClick(item.id)}
                    className={`${tooltipHackClasses} ${
                      isActiveTab
                        ? '!bg-[var(--ds-inner-tab-hover-and-active-bg)]'
                        : '!bg-[var(--ds-inner-tab-inactive-bg)]'
                    } flex justify-between hover:!bg-[var(--ds-inner-tab-hover-and-active-bg)] w-[200px] rounded-md flex items-center h-[var(--ds-tab-sizing-height)] min-w-0 px-2 gap-2 focus:outline-none text-ds-400 leading-ds-300 transition-colors`}
                    data-testid="inner-tab-menu-item"
                  >
                    <TooltipBubble
                      title={item.hoverTitle || item.title}
                      placement="top"
                    >
                      <TextCaption
                        label={item.title}
                        color="text-ds-black"
                        ellipsis
                      />
                    </TooltipBubble>
                    <span
                      className={`shrink-0 cursor-pointer [&>svg]:text-current [&>svg]:transition-[fill] ${getCloseIconClasses(
                        isActiveTab
                      )}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onTabClose?.(item.id);
                      }}
                      data-testid="inner-tab-menu-item-close"
                    >
                      <CloseIcon />
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

export default MoreInnerTabsDropdown;
